import {
  all, call, select, takeLatest, put
} from 'redux-saga/effects';
import { fromJS } from 'immutable';
import { push } from 'connected-react-router';

import defaultFetch from 'react/api/sagas/fetch/defaultFetch.js';
import { actions as integrationActions } from 'react/api/actions/integrations';
import { getResourceTo } from 'react/utils/utils';
import { getIntegration } from 'react/api/selectors/integrations';

import IntegrationLogsFeedbackService from 'react/services/integrationLogsFeedback.service';

const {
  clearIntegrationConnectState,
  clearIntegrationDisableState,
  setConnectionState,
  setDisableState,
  setIsLoadingIntegrations,
  connectIntegration,
  disableIntegration,
  fetchIntegrations,
  fetchIntegrationLogs,
  addIntegration,
  updateIntegrations,
  updateIntegrationLogs,
  updateSingleIntegration,
  clearIntegrationLogs,
  retryIntegrationLogs,
  toggleRetryIntegrationLogsConfirmModal,
  setRetryLogsState,
  openRetriedLogsPage,
  updateEventNotifierSSEStatus
} = integrationActions;

function* startEventNotifierSSE(tenantId, url) {
  const integrationsLogFeedbackService = new IntegrationLogsFeedbackService(tenantId, url);
  integrationsLogFeedbackService.connect();

  Monet.Session.IntegrationsLogsFeedback = integrationsLogFeedbackService;

  yield put({
    type: updateEventNotifierSSEStatus,
    payload: true
  });
}

function* disableEventNotifierSSE() {
  if (!Monet.Session.IntegrationsLogsFeedback) return;

  Monet.Session.IntegrationsLogsFeedback.closeConnection();

  yield put({
    type: updateEventNotifierSSEStatus,
    payload: false
  });
}

function* handleEventNotifierSSE() {
  const { integrationsList, eventNotifierSSEIsOpen, tenantId } = yield select(state => ({
    integrationsList: state.integrations.get('integrations', []),
    eventNotifierSSEIsOpen: state.integrations.get('eventNotifierSSEIsOpen', false),
    tenantId: state.configuration?.globalConfiguration?.authConfigId
  }));

  const hasAtLeastOneIntegrationEnabled = integrationsList.some(item => item.get('enabled'));

  if (hasAtLeastOneIntegrationEnabled && !eventNotifierSSEIsOpen) {
    const internalUrl = integrationsList
      .find(item => item.get('internalUrl'))
      .get('internalUrl');

    yield startEventNotifierSSE(tenantId, internalUrl);
  } else if (eventNotifierSSEIsOpen) {
    yield disableEventNotifierSSE();
  }
}


function* enableEntitiesFromIntegration({ integration }) {
  const entitiesToEnable = integration.entities.map((item) => ({
    className: item.className,
    eventTypes: ["INSERT", "UPDATE", "UPDATE_BY_PROPERTIES"]
  }));

  if (entitiesToEnable.length > 0) {
    yield call(defaultFetch, '/Audit', entitiesToEnable, 'POST');
  }
}

function* disableEntitiesFromIntegration({ integration }) {
  const entitiesToDisable = integration.entities.map(({ className }) => className);

  if (entitiesToDisable.length > 0) {
    yield call(defaultFetch, '/Audit/Classes', entitiesToDisable, 'DELETE');
  }
}

function* connectIntegrationSaga(action) {
  const integrationData = action.payload;
  const { createdStatically, enabled } = integrationData;

  const requestMethod = createdStatically ? 'POST' : 'PATCH';
  const requestUrl = createdStatically
    ? '/EventNotifier/Configurations'
    : `/EventNotifier/Configurations/${integrationData.id}`;

  const encodedURL = encodeURI(requestUrl);

  yield put({
    type: setConnectionState,
    payload: {
      isConnecting: true,
      success: null,
      error: null
    }
  });

  const connectIntegrationResponse = yield call(
    defaultFetch,
    encodedURL,
    {
      ...integrationData,
      enabled: true
    },
    requestMethod
  );

  if (connectIntegrationResponse?.error) {
    yield all([
      put({
        type: setConnectionState,
        payload: {
          isConnecting: false,
          error: {
            message: getResourceTo('IntegrationModal.ConnectIntegration.ErrorMessage')
          },
          success: null
        }
      })
    ]);
  } else {
    const generatorsToPerform = [
      put({
        type: updateSingleIntegration,
        payload: {
          ...connectIntegrationResponse,
          createdStatically: false
        }
      }),
      put({
        type: setConnectionState,
        payload: {
          isConnecting: false,
          error: null,
          success: {
            message: getResourceTo('IntegrationModal.ConnectIntegration.SuccessMessage')
          }
        }
      }),
    ];

    if (createdStatically || !enabled) {
      const enableEntitiesFromIntegrationGenerator = enableEntitiesFromIntegration(connectIntegrationResponse);

      generatorsToPerform.push(enableEntitiesFromIntegrationGenerator);
    }

    yield all(generatorsToPerform);
    yield handleEventNotifierSSE();
  }
}

function* watchConnectIntegration() {
  yield takeLatest(connectIntegration, connectIntegrationSaga);
}

function* disableIntegrationSaga(action) {
  const integrationData = action.payload;
  const { id } = integrationData;

  const encodedURL = encodeURI(`/EventNotifier/Configurations/${id}`);
  const payload = {
    enabled: false
  };

  yield put({
    type: setDisableState,
    payload: {
      isDisabling: true,
      error: null
    }
  });

  const disableIntegrationResponse = yield call(defaultFetch, encodedURL, payload, 'PATCH');

  if (disableIntegrationResponse?.error) {
    yield all([
      put({
        type: setDisableState,
        payload: {
          isDisabling: false,
          error: {
            message: getResourceTo('IntegrationModal.DisableIntegrationModal.ErrorMessage')
          }
        }
      })
    ]);
  } else {
    yield all([
      put({
        type: updateSingleIntegration,
        payload: disableIntegrationResponse
      }),
      put({
        type: setDisableState,
        payload: {
          isDisabling: false,
          error: null
        }
      }),
      disableEntitiesFromIntegration(disableIntegrationResponse)
    ]);

    yield handleEventNotifierSSE();
  }
}

function* watchDisableIntegration() {
  yield takeLatest(disableIntegration, disableIntegrationSaga);
}

function* updateIntegrationLogData({
  integrationId,
  integrationLogs,
  shouldClearData,
  shouldClearBeforeUpdateLogs
}) {
  if (shouldClearData) {
    yield put({
      type: clearIntegrationLogs,
      payload: {
        integrationId
      }
    });
  } else {
    yield put({
      type: updateIntegrationLogs,
      payload: {
        integrationId,
        integrationLogs,
        shouldClearBeforeUpdateLogs
      }
    });
  }
}

function* addIntegrationToStateIfItNotExists(integrationId) {
  const findIntegration = yield select((state) => getIntegration(state, integrationId));

  if (!findIntegration) {
    const integration = yield fetchSingleIntegration(integrationId);

    yield put({
      type: addIntegration,
      payload: integration
    });
  }
}

function* fetchIntegrationLogsSaga(action) {
  const { filtersPayload } = action.payload;
  const integrationId = filtersPayload?.configuration;

  if (!integrationId) {
    return;
  }

  const encodedURL = encodeURI('/EventNotifier/Logs');
  const requestBody = Object.assign({
    "configuration" : integrationId,
    "page" : 0,
    "size" : 51,
  }, filtersPayload);

  const integrationLogs = yield call(defaultFetch, encodedURL, requestBody, 'POST');

  if (!integrationLogs?.error) {
    const shouldClearData = requestBody.page === 0 && !integrationLogs.length;
    const shouldClearBeforeUpdateLogs =  requestBody.page === 0 && integrationLogs.length;

    yield addIntegrationToStateIfItNotExists(integrationId);
    yield updateIntegrationLogData({
      integrationId,
      integrationLogs,
      shouldClearData,
      shouldClearBeforeUpdateLogs
    });
  }

  App.Forms[`IntegrationLog_${integrationId}`]?.filterBuilder?.loading?.hide();
}

function* watchFetchIntegrationLogs() {
  yield takeLatest(fetchIntegrationLogs, fetchIntegrationLogsSaga);
}

function* fetchIntegrationsSaga() {
  yield put({
    type: setIsLoadingIntegrations,
    payload: {
      isLoadingIntegrations: true
    }
  });

  const encodedURL = encodeURI('/EventNotifier/Configurations');
  const fetchIntegrationsResponse = yield call(defaultFetch, encodedURL);

  if (fetchIntegrationsResponse?.error) {
    yield put({
      type: setIsLoadingIntegrations,
      payload: {
        isLoadingIntegrations: false
      }
    });
  } else {
    const defaultIntegration = {
      integration: {
        name: "RoutePlannerFeedback",
        type: "OD",
        authType: "BASIC"
      },
      enabled: false,
      url: "",
      credentials: {
        "username": ""
      },
      retryAttempts: 2,
      retryBackoff: 5000,
      createdStatically: true
    };
    

    const integrations = fetchIntegrationsResponse?.length ? fetchIntegrationsResponse : [defaultIntegration];

    yield all([
      put({
        type: updateIntegrations,
        payload: {
          integrations
        }
      }),
      put({
        type: setIsLoadingIntegrations,
        payload: {
          isLoadingIntegrations: false
        }
      })
    ]);

    yield handleEventNotifierSSE();
  }
}

function* watchFetchIntegrations() {
  yield takeLatest(fetchIntegrations, fetchIntegrationsSaga);
}

function* fetchSingleIntegration(integrationId) {
  if (!integrationId)
    return;

  const encodedURL = encodeURI(`/EventNotifier/Configurations/${integrationId}`);
  const fetchIntegrationResponse = yield call(defaultFetch, encodedURL);

  if (!fetchIntegrationResponse?.error) {
    return fetchIntegrationResponse;
  }

  return null;
}

function* watchClearConnectStatus() {
  yield takeLatest(clearIntegrationConnectState, function* () {
    yield put({
      type: setConnectionState,
      payload: {
        isConnecting: false,
        success: null,
        error: null
      }
    });
  });
}

function* watchClearDisableStatus() {
  yield takeLatest(clearIntegrationDisableState, function* () {
    yield put({
      type: setDisableState,
      payload: {
        isDisabling: false,
        error: null
      }
    });
  });
}

function* retryIntegrationLogsSaga(action) {
  const { integrations } = yield select();
  const logsToRetry = integrations.getIn(['retryLogsState', 'logsToRetry'], fromJS([]));
  const integrationLogsIds = logsToRetry.toJS();

  if (!integrationLogsIds.length) {
    return ;
  }

  yield put({
    type: setRetryLogsState,
    payload: {
      logsToRetry: integrationLogsIds,
      isRetrying: true,
      error: null,
    }
  });

  const encodedURL = encodeURI('/EventNotifier/Logs/Retry');
  const retryIntegrationLogsResponse = yield call(defaultFetch, encodedURL, integrationLogsIds, 'POST', false);

  if (retryIntegrationLogsResponse?.status === 200) {
    yield all([
      put({
        type: setRetryLogsState,
        payload: {
          logsToRetry: [],
          isRetrying: false,
          error: null
        }
      }),
      put({
        type: toggleRetryIntegrationLogsConfirmModal,
      })
    ]);
  } else {
    const requestErrorMessage = retryIntegrationLogsResponse?.error?.errorMessages[0]?.i18nMessage || null;
    const defaultErrorMessage = getResourceTo('RetryIntegrations.Modal.DefaultError');

    yield put({
      type: setRetryLogsState,
      payload: {
        logsToRetry: integrationLogsIds,
        isRetrying: false,
        error: {
          message: requestErrorMessage || defaultErrorMessage
        }
      }
    });
  }
}

function* watchRetryIntegrationLogs() {
  yield takeLatest(retryIntegrationLogs, retryIntegrationLogsSaga);
}

function* openRetriedIntegrationsLogsSaga(action) {
  const { integrationId, records, pageId } = action.payload;

  const findIntegration = yield select((state) => getIntegration(state, integrationId));

  if (!findIntegration) {
    const integration = yield fetchSingleIntegration(integrationId);

    yield put({
      type: addIntegration,
      payload: integration
    });
  }

  yield put({
    type: updateIntegrationLogs,
    payload: {
      integrationId: pageId,
      integrationLogs: records,
      shouldClearBeforeUpdateLogs: true
    }
  });

  yield put(push(`/Integrations/retriedLogs/${integrationId}`));
}

function* watchOpenRetriedIntegrationsLogs() {
  yield takeLatest(openRetriedLogsPage, openRetriedIntegrationsLogsSaga);
}

export default function* () {
  yield all([
    watchFetchIntegrations(),
    watchFetchIntegrationLogs(),
    watchConnectIntegration(),
    watchClearConnectStatus(),
    watchDisableIntegration(),
    watchClearDisableStatus(),
    watchRetryIntegrationLogs(),
    watchOpenRetriedIntegrationsLogs()
  ]);
}
